import type { Visit } from '../readinessMock';
import { blockTime, labelDayShort, visitsForDate } from '../readinessMock';

const DAYBEFORE_SHOWN = 5;

/**
 * The week agenda (rcm-23): not a time grid — one card per day, the visits that
 * aren't ready on top ("Known now"), then the earliest few that ELVA checks the
 * day before. A row opens that day in Day view with the visit selected.
 */
export function WeekAgenda({
  days,
  providerId,
  filterNotReady,
  onOpenVisit,
  onOpenDay,
}: {
  days: string[];
  providerId: string;
  filterNotReady: boolean;
  onOpenVisit: (iso: string, visitId: string) => void;
  onOpenDay: (iso: string) => void;
}) {
  return (
    <>
      <div className="rcal-wk">
        {days.map((iso) => (
          <DayCard
            key={iso}
            iso={iso}
            providerId={providerId}
            filterNotReady={filterNotReady}
            onOpenVisit={onOpenVisit}
            onOpenDay={onOpenDay}
          />
        ))}
      </div>
      <div className="rcal-legend" style={{ marginTop: 16 }}>
        <span>
          <i style={{ background: '#c98a1b' }} />
          Not ready: ELVA already knows something is missing
        </span>
        <span>
          <i style={{ background: '#e6ebe9' }} />
          Other visits: ELVA checks each one the day before
        </span>
      </div>
    </>
  );
}

function matchProvider(v: Visit, providerId: string): boolean {
  return providerId === 'all' || v.providerId === providerId;
}

function DayCard({
  iso,
  providerId,
  filterNotReady,
  onOpenVisit,
  onOpenDay,
}: {
  iso: string;
  providerId: string;
  filterNotReady: boolean;
  onOpenVisit: (iso: string, visitId: string) => void;
  onOpenDay: (iso: string) => void;
}) {
  const all = visitsForDate(iso).filter((v) => matchProvider(v, providerId));
  const knownRows = all.filter((v) => v.known);
  const notReadyRows = knownRows.filter((v) => v.known?.kind === 'not_ready');
  const otherRows = all.filter((v) => !v.known).sort((a, b) => a.startMin - b.startMin);
  const notReadyCount = notReadyRows.length;

  if (all.length === 0) {
    return (
      <div className="rcal-dc">
        <h3>{labelDayShort(iso)}</h3>
        <div className="rcal-dm">No visits</div>
      </div>
    );
  }

  const shownKnown = filterNotReady ? notReadyRows : knownRows;
  const dayBefore = otherRows.slice(0, DAYBEFORE_SHOWN);
  const moreCount = otherRows.length - dayBefore.length;

  return (
    <div className="rcal-dc">
      <h3>{labelDayShort(iso)}</h3>
      <div className="rcal-dm">
        {all.length} {all.length === 1 ? 'visit' : 'visits'}
        {notReadyCount > 0 ? ` · ${String(notReadyCount)} not ready` : ''}
      </div>

      {filterNotReady && notReadyRows.length === 0 ? (
        <div className="rcal-allset">All set so far</div>
      ) : null}

      {shownKnown.length > 0 ? (
        <>
          <div className="rcal-gl">Known now</div>
          {shownKnown.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`rcal-ar${v.known?.kind === 'not_ready' ? ' warn' : ''}`}
              onClick={() => {
                onOpenVisit(iso, v.id);
              }}
            >
              <span className="rcal-vt">{blockTime(v.startMin)}</span>
              <b>{v.patient}</b> · <span className="rcal-proc">{v.procedure.label}</span>
              <span className="rcal-vr">{v.known?.reason}</span>
            </button>
          ))}
        </>
      ) : null}

      {!filterNotReady && dayBefore.length > 0 ? (
        <>
          <div className="rcal-gl">Checks the day before</div>
          {dayBefore.map((v) => (
            <button
              key={v.id}
              type="button"
              className="rcal-ar"
              onClick={() => {
                onOpenVisit(iso, v.id);
              }}
            >
              <span className="rcal-vt">{blockTime(v.startMin)}</span>
              {v.patient} · <span className="rcal-proc">{v.procedure.label}</span>
            </button>
          ))}
          {moreCount > 0 ? (
            <button
              type="button"
              className="rcal-more"
              onClick={() => {
                onOpenDay(iso);
              }}
            >
              {moreCount} more {moreCount === 1 ? 'visit' : 'visits'}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** 5 card skeletons (rcm-23 §5). */
export function WeekSkeleton() {
  return (
    <div className="rcal-wk">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="rcal-dc">
          <div className="rcal-skel" style={{ position: 'relative', height: 20, width: '60%', marginBottom: 10 }} />
          <div className="rcal-skel" style={{ position: 'relative', height: 48, marginBottom: 8 }} />
          <div className="rcal-skel" style={{ position: 'relative', height: 160 }} />
        </div>
      ))}
    </div>
  );
}
